import { Extend } from './internal/typeHelpers';
import type { PersistedExternalDocument } from './externalDocumentApiTypes';
import { LocallyDerivedTransformation } from './locallyDerivedTransformationsApiTypes';
import type { RelativeDateValue } from './advancedSearchApiTypes';
import type { ArdoqId, Email, Entity, Versioned, MetaData, Persisted, Url, VersionedEntity, } from './coreTypes';
// Trigger tests
export type Token = string;
export type Described = {
    description: string | null;
};
export type Named = {
    name: string;
};
export type Lockable = {
    lock?: ArdoqId | null;
};
export type NamedEntity = Entity & Named;
export type RenameRequest = Entity & Named & Versioned;
export type WithoutMetadata<T> = Omit<T, keyof MetaData | keyof ArdoqBlob>;
export type CamelizedEntityTypeBlob<T> = {
    entityType: T;
};
type LispyEntityTypeBlob<T> = {
    'entity-type': T;
};
type DefaultEntityTypeBlob<T> = CamelizedEntityTypeBlob<T> | LispyEntityTypeBlob<T>;
export type ArdoqBlob<T extends object = DefaultEntityTypeBlob<APIEntityType>, Blob extends object = Record<string, any>> = {
    ardoq: T & Blob & {
        persistent?: Record<any, any> | null;
        valid?: boolean;
        namePath?: string[];
    };
};
export enum LineType {
    SOLID = 'solid',
    DOTTED = 'dotted',
    DASHED = 'dashed'
}
export enum ArrowType {
    NONE = 'none',
    BOTH = 'both',
    BOTH_START = 'bothStart',
    DIAMOND = 'diamond',
    DIAMOND_START = 'diamondStart',
    DIAMOND_FILLED = 'diamondFilled',
    DIAMOND_FILLED_START = 'diamondFilledStart',
    TOP = 'top',
    TOP_START = 'topStart',
    BOTTOM = 'bottom',
    BOTTOM_START = 'bottomStart',
    BOTH_FILLED = 'bothFilled',
    BOTH_FILLED_START = 'bothFilledStart',
    BOTH_OUTLINED = 'bothOutlined',
    BOTH_OUTLINED_START = 'bothOutlinedStart',
    CIRCLE = 'circle',
    CIRCLE_START = 'circleStart',
    ONE = 'one',
    ONE_START = 'oneStart',
    ONE_MANDATORY = 'oneMandatory',
    ONE_MANDATORY_START = 'oneMandatoryStart',
    MANY = 'many',
    MANY_START = 'manyStart',
    ONE_OR_MANY = 'oneOrMany',
    ONE_OR_MANY_START = 'oneOrManyStart',
    ZERO_OR_ONE = 'zeroOrOne',
    ZERO_OR_ONE_START = 'zeroOrOneStart',
    ZERO_OR_MANY = 'zeroOrMany',
    ZERO_OR_MANY_START = 'zeroOrManyStart',
    DEFAULT = 'default',
    DEFAULT_START = 'defaultStart'
}
export enum LoginOptions {
    PLAIN = 'plain',
    GITHUB = 'github',
    LIVE_CONNECT = 'live-connect',
    GOOGLE = 'google',
    OKTA = 'okta',
    SAFEWHERE = 'safewhere',
    PING = 'ping',
    NETIQ = 'netiq',
    ACTIVE_DIRECTORY = 'active-directory',
    ACTIVE_DIRECTORY_STAGING = 'active-directory-staging',
    ACTIVE_DIRECTORY_GAMMA = 'active-directory-gamma',
    // Todo ask appsec which one we can remove?
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    NET_IQ = 'netiq',
    OPEN_AM = 'openam',
    ADFS = 'adfs',
    ILEX = 'ilex',
    ONELOGIN = 'onelogin',
    SAML = 'saml',
    SAML_ADFS = 'samladfs',
    SAML_OKTA = 'samlokta',
    SHIBBOLETH = 'shibboleth',
    MICROSOFTONLINE = 'microsoftonline',
    OAUTH2 = 'oauth2',
    OIC = 'oic'
}
export enum AssetType {
    TRAVERSAL = 'traversal',
    FOLDER = 'workspacefolder',
    WORKSPACE = 'workspace',
    PRESENTATION = 'presentation',
    SURVEY = 'survey',
    METAMODEL = 'metamodel',
    SCENARIO = 'scenario',
    REPORT = 'report',
    DASHBOARD = 'dashboard',
    BROADCAST = 'broadcast',
    VIEWPOINT = 'viewpoint',
    BOOKMARK = 'bookmark'
}
const ALL_ASSET_TYPES = Object.values(AssetType);
export const getAllAssetTypes = (): AssetType[] => ALL_ASSET_TYPES;
export enum ResourceType {
    AI_GENERATE_QUERY = 'nl-to-advanced-search',
    BOOKMARK = 'bookmark',
    BROADCAST = 'broadcast',
    CHAT = 'chat',
    COMPONENT = 'component',
    DASHBOARD = 'dashboard',
    DOCUMENT_ARCHIVE_FOLDER = 'folder',
    DOCUMENT_ARCHIVE_ATTACHMENT = 'attachment',
    ENTITY_GROUP = 'entity-group',
    EXTRACTION_DRAFT = 'extraction-draft',
    FIELD = 'field',
    FUTURE_STATE = 'future-state',
    INITIATIVE = 'initiative',
    METAMODEL = 'metamodel',
    MODEL = 'model',
    ORG_TRIPLE = 'org-triple',
    ORGANIZATION = 'organization',
    PERMISSION = 'permission',
    PRESENTATION = 'presentation',
    REFERENCE = 'reference',
    REPORT = 'report',
    SCENARIO = 'scenario',
    SLIDE = 'slide',
    SURVEY = 'survey',
    TAG = 'tag',
    TRAVERSAL = 'traversal',
    VIEWPOINT = 'viewpoint',
    WORKSPACE = 'workspace',
    WORKSPACEFOLDER = 'workspacefolder'
}
/**
 * @deprecated Redundant and provides no value, use `PermissionOrgGroup` instead.
 * "org/role", e.g. "piedpiper/member"
 */
export type PermissionRole = `${string}/${string}`;
/**
 * "org/label", e.g "piedpiper/writer" or "piedpiper/admin"
 */
export type PermissionOrgGroup = `${string}/${string}`;
/**
 * `OrgAccessLevel` enum represents the level of access a user has
 * within the organization.
 *
 * `PermissionAccessLevel` enum represents the level of access a user
 * or group has on specific entities like workspaces and presentations.
 *
 * Despite the potential confusion between these two enums, we are keeping
 * them as they are due to their extensive use throughout the codebase.
 * It's important to understand the context in which they are used to
 * differentiate between them.
 */
export enum OrgAccessLevel {
    ADMIN = 'admin',
    WRITER = 'writer',
    READER = 'reader',
    CONTRIBUTOR = 'contributor'
}
export type MetamodelComponentTypeStyle = {
    color?: string;
    icon?: string;
    image?: string;
    shape?: string;
    level?: number;
};
export type NameAndId = {
    id: ArdoqId;
    name: string;
};
type IdHash<T> = {
    [id: string]: T;
};
type MetamodelInstanceStats = {
    instanceCount: number;
};
type MetamodelFieldStats = {
    fieldUsage: {
        [field: string]: number;
    };
    fieldCompleteness: number;
};
export type MetamodelAggregatedElement = NameAndId & {
    workspaceId: ArdoqId;
    metadata: MetamodelInstanceStats & MetamodelFieldStats;
};
type MetamodelReferenceTypeStyle = {
    color?: string;
    line?: LineType;
    lineBeginning?: ArrowType;
    lineEnding?: ArrowType;
};
type MetamodelReferenceCompletenessStats = {
    referenceCompleteness: {
        source: {
            totalComponents: number;
            connectedComponents: number;
        };
        target: {
            totalComponents: number;
            connectedComponents: number;
        };
    };
};
/**
 * A reference type that is included in the metamodel
 *
 * This reference type is an aggregation reference instances with the same:
 *  - source component type
 *  - reference type
 *  - target component type
 *
 * The id should be a concatenation of workspace, reference type and source and
 * target type, like this:
 * ```
 *   {workspaceId}-{referenceTypeId}-{sourceTypeId}-{targetTypeId}
 * ```
 */
export type MetamodelReferenceType = MetamodelAggregatedElement & {
    typeId: string | number;
    source: ArdoqId;
    /**
     * The target of the reference type
     *
     * May be undefined if it targets a component in a workspace not included in
     * this metamodel.
     */
    target: ArdoqId;
    style: MetamodelReferenceTypeStyle;
    metadata: MetamodelInstanceStats & MetamodelFieldStats & MetamodelReferenceCompletenessStats;
};
/**
 * A component type that is included in the metamodel
 *
 * The id should be the typeId of the component type.
 */
export type MetamodelComponentType = MetamodelAggregatedElement & {
    /**
     * Component type above this component type in the Workspace model hierarchy
     */
    parent?: ArdoqId;
    style: MetamodelComponentTypeStyle;
};
/**
 * A workspace that is included in the metamodel
 *
 * The workspace represents itself and is not aggregated.
 */
export type MetamodelWorkspace = NameAndId & {
    folder?: ArdoqId;
    modelId: ArdoqId;
};
/**
 * A workspace folder that is included in the metamodel
 *
 * The workspace folder represents itself and is not aggregated.
 */
export type MetamodelAssetFolder = NameAndId;
/**
 * The calculated or aggregated parts of a metamodel
 */
export type MetamodelContent = {
    workspaceFolders: IdHash<MetamodelAssetFolder>;
    workspaces: IdHash<MetamodelWorkspace>;
    componentTypes: IdHash<MetamodelComponentType>;
    referenceTypes: IdHash<MetamodelReferenceType>;
    dateRangeFieldMap?: DateRangeFieldMapType;
};
/**
 * The user defined parts of a metamodel
 */
export type MetamodelBase = {
    name: string;
    workspaceIds: ArdoqId[];
};
export type Metamodel = MetamodelBase & {
    metamodel: MetamodelContent;
};
export type PersistedMetamodel = Persisted<Metamodel> & {
    wholeOrg: boolean;
};
export enum PermissionAccessLevel {
    ADMIN = 'admin',
    EDIT = 'edit',
    READ = 'read'
}
export enum SurveyPermissionAccessLevel {
    CONTRIBUTE = 'contribute'
}
export type PredefinedBasedOnRole = 'everyone' | 'member' | 'contributor';
/**
 * Type of a single users group.
 */
export type PermissionGroup = ArdoqBlob<CamelizedEntityTypeBlob<APIEntityType.GROUP>> & {
    _id: ArdoqId;
    name: string;
    label: string;
    users: ArdoqId[];
    membershipFromSso?: ArdoqId[];
    predefinedBasedOnRole?: PredefinedBasedOnRole;
};
export type SystemPermissionGroup = Extend<PermissionGroup, {
    predefinedBasedOnRole: PredefinedBasedOnRole ;
}>;
export enum UserManagementSettings {
    IN_APP_ONLY = 'in-app-only',
    IN_APP_AND_EXTERNALLY = 'in-app-and-externally',
    EXTERNALLY = 'externally'
}
export enum OrganizationKeys {
    CUSTOM_DOMAIN = 'custom-domain',
    ACTIVE_DIRECTORY = 'active-directory-settings',
    IS_TRIAL = 'is-trial',
    DAYS_REMAINING_TRIAL = 'days-remaining-trial',
    NAME = 'name',
    FEATURES = 'features',
    SETTINGS = 'settings',
    HAS_CONSENTED_TO_VIEW_DATA = 'hasConsentedToViewData'
}
export enum CustomDomainKeys {
    BASE_URL = 'base-url',
    LOGIN_OPTIONS = 'login-options',
    USER_MANAGEMENT = 'user-management',
    DEFAULT_ROLE = 'default-role'
}
export type CustomDomain = {
    [CustomDomainKeys.BASE_URL]: string;
    [CustomDomainKeys.LOGIN_OPTIONS]: Record<LoginOptions, boolean>;
    [CustomDomainKeys.USER_MANAGEMENT]: UserManagementSettings;
    [CustomDomainKeys.DEFAULT_ROLE]: OrgAccessLevel | null;
};
export type UpdateUserManagementSettingsRequest = {
    defaultRole: OrgAccessLevel | null;
    userManagement: UserManagementSettings;
};
enum ActiveDirectoryKeys {
    SINGLE_TENANT = 'single-tenant?',
    SCOPES = 'scopes',
    TENANT_ID = 'tenant-id',
    POST_LOGOUT_REDIRECT = 'post-logout-redirect'
}
type ActiveDirectory = {
    [ActiveDirectoryKeys.SINGLE_TENANT]: boolean;
    [ActiveDirectoryKeys.SCOPES]: string[];
    [ActiveDirectoryKeys.TENANT_ID]: string | null;
    [ActiveDirectoryKeys.POST_LOGOUT_REDIRECT]: boolean;
};
export type Organization = {
    _id: string;
    name: string;
    label: string;
    [OrganizationKeys.CUSTOM_DOMAIN]: CustomDomain | null;
    role: string;
    members: number;
    [OrganizationKeys.IS_TRIAL]: boolean;
    [OrganizationKeys.DAYS_REMAINING_TRIAL]?: number;
    expired: boolean;
    [OrganizationKeys.ACTIVE_DIRECTORY]: ActiveDirectory | null;
    [OrganizationKeys.FEATURES]: Record<string, boolean>;
    [OrganizationKeys.SETTINGS]: {
        defaultWsPermissions: PermissionAccessLevel | null;
        logo?: string;
        emergencyDataSecurityContacts?: string[];
        ipAllowlistDeniedAccessMessage?: string;
    };
    db: {
        name: string;
    };
    [OrganizationKeys.HAS_CONSENTED_TO_VIEW_DATA]?: boolean;
    sandbox: boolean;
};
export enum ViewIds {
    BLOCK_DIAGRAM = 'blockDiagram',
    BUBBLE = 'bubble',
    CAPABILITY_MAP = 'capabilityMap',
    COMPONENTTREE = 'componenttree',
    DEPMATRIX = 'depmatrix',
    DEPWHEEL = 'depwheel',
    REFERENCETABLE = 'referenceTable',
    ROADMAP = 'roadmap',
    SPIDER = 'spider',
    SWIMLANE = 'swimlane',
    TABLEVIEW = 'tableview',
    TAGSCAPE = 'tagscape',
    TIMELINE = 'timeline',
    TREEMAP = 'treemap',
    PAGESVIEW = 'pagesView',
    COMPONENT_MATRIX = 'componentMatrix',
    COMPONENT_MATRIX_2 = 'componentMatrix2',
    DEPENDENCY_MAP_2 = 'dependencyMap',
    NETWORK = 'relationships2',
    RELATIONSHIPS_3 = 'relationships3',
    ISOMETRIC_BLOCK_DIAGRAM = 'isometricBlockDiagram',
    NONE = '',
    SIMPLE_BLOCK_DIAGRAM = 'simpleBlockDiagram',
    HIERARCHICAL_TREEMAP = 'hierarchicalTreemap',
    HIERARCHICAL_PACKING = 'hierarchicalPacking',
    SUNBURST = 'sunburst',
    METAMODEL = 'metamodel',
    BLOCKS = 'blocks',
    ASSISTANT_DIAGRAM = 'assistantDiagram',
    ASSISTANT_BLOCKS = 'assistantBlocks',
    FLEXIBLE_BLOCK_DIAGRAM = 'flexibleBlockDiagram',
    // #region discontinued views
    CIRCULAR_RELATIONSHIP_DIAGRAM = 'explorer',
    SEQUENCE = 'sequence',
    REFERENCE_AGGREGATION_VIEW = 'referenceAggregationView',
    PROCESSFLOW = 'processflow',
    HEATMAP = 'heatmap',
    API_DOCUMENTATION = 'apiDocumentation',
    /** @deprecated this ViewId is deprecated! not to be mistaken for ViewIds.RELATIONSHIPS_3 or ViewIds.NETWORK */
    RELATIONSHIPS = 'relationships',
    DEPMAP = 'depmap',
    MODERNIZED_BLOCK_DIAGRAM = 'modernizedBlockDiagram'
}
export enum ViewSettingsContexts {
    VIEWPOINT_MODE = 'viewpointMode',
    WORKSPACE_MODE = 'workspaceMode'
}
export enum HTTPStatusCode {
    'CONTINUE' = 100,
    'SWITCHING_PROTOCOLS' = 101,
    'OK' = 200,
    'CREATED' = 201,
    'ACCEPTED' = 202,
    'NON_AUTHORITATIVE_INFORMATION' = 203,
    'NO_CONTENT' = 204,
    'RESET_CONTENT' = 205,
    'PARTIAL_CONTENT' = 206,
    'MULTIPLE_CHOICES' = 300,
    'MOVED_PERMANENTLY' = 301,
    'FOUND' = 302,
    'SEE_OTHER' = 303,
    'NOT_MODIFIED' = 304,
    'USE_PROXY' = 305,
    'TEMPORARY REDIRECT' = 307,
    'BAD_REQUEST' = 400,
    'UNAUTHORIZED' = 401,
    'PAYMENT_REQUIRED' = 402,
    'FORBIDDEN' = 403,
    'NOT_FOUND' = 404,
    'METHOD_NOT_ALLOWED' = 405,
    'NOT_ACCEPTABLE' = 406,
    'PROXY_AUTHENTICATION_REQUIRED' = 407,
    'REQUEST_TIMEOUT' = 408,
    'CONFLICT' = 409,
    'GONE' = 410,
    'LENGTH_REQUIRED' = 411,
    'PRECONDITION_FAILED' = 412,
    'REQUEST_ENTITY_TOO_LARGE' = 413,
    'REQUEST-URI_TOO_LONG' = 414,
    'UNSUPPORTED_MEDIA_TYPE' = 415,
    'REQUESTED_RANGE_NOT_SATISFIABLE' = 416,
    'EXPECTATION_FAILED' = 417,
    'UNPROCESSABLE_ENTITY' = 422,
    'TOO_MANY_REQUESTS' = 429,
    'INTERNAL_SERVER_ERROR' = 500,
    'NOT_IMPLEMENTED' = 501,
    'BAD_GATEWAY' = 502,
    'SERVICE_UNAVAILABLE' = 503,
    'GATEWAY_TIMEOUT' = 504,
    'HTTP_VERSION_NOT_SUPPORTED' = 505,
    'UNKNOWN_ERROR' = 520
}
export type AddToScenarioResponse = {
    componentIds: ArdoqId[];
    referenceIds: ArdoqId[];
    workspaceIds: ArdoqId[];
};
export type APIResetData<APIAttributeModelType> = {
    [Verb.CREATE]: APIAttributeModelType[];
    [Verb.DELETE]: ArdoqId[];
    [Verb.UPDATE]: APIAttributeModelType[];
};
export type UpdateScenarioPayload = {
    scenarioId: ArdoqId;
    componentIds: ArdoqId[];
    referenceIds: ArdoqId[];
};
export type GraphSearchResultObject = {
    id: string;
    label: string;
    properties: { 
        name: {
            value: string;
        }[];
    };
}[];
export type GraphSearchRunResponse<T> = {
    status: string;
    error: string;
    /**
     * Total number of results returned by query
     */
    count: number;
    /**
     * Number of results in this paginated response
     */
    size: number;
    result: T;
};
export type FieldUsageWorkspace = {
    workspaceId: ArdoqId;
    affectedComponentIds: ArdoqId[];
    affectedReferenceIds: ArdoqId[];
};
export enum APIEntityType {
    COMPONENT = 'component',
    FIELD = 'field',
    MODEL = 'model',
    PERMISSION = 'permission',
    REFERENCE = 'reference',
    TAG = 'tag',
    WORKSPACE = 'workspace',
    COMPONENT_TYPE = 'component-type',
    REFERENCE_TYPE = 'reference-type',
    ATTACHMENT = 'attachment',
    PERSPECTIVE = 'perspective',
    WORKSPACE_FOLDER = 'workspacefolder',
    DOCUMENT_ARCHIVE_FOLDER = 'folder',
    DASHBOARD = 'dashboard',
    REPORT = 'report',
    TRAVERSAL = 'traversal',
    SLIDE = 'slide',
    PRESENTATION = 'presentation',
    CHANGE_REQUEST = 'change-request',
    SCENARIO = 'scenario',
    CALL_TO_ACTION = 'call-to-action',
    GROUP = 'group'
}
export enum PrivilegeLabel {
    ACCESS_DISCOVER = 'access-discover',
    CONFIGURE_PRIVILEGES = 'configure-privileges',
    CREATE_SCENARIO = 'create-scenario',
    CREATE_SURVEY = 'create-survey',
    CREATE_DASHBOARD = 'create-dashboard',
    CREATE_REPORT = 'create-report',
    GLOBAL_DATA_ACCESS = 'global-data-access',
    CREATE_VIEWPOINT = 'create-viewpoint',
    CREATE_WORKSPACE = 'create-workspace',
    CREATE_GREMLIN_QUERIES = 'create-gremlin',
    READ_GROUPS = 'read-groups',
    MANAGE_GROUPS = 'manage-groups',
    CREATE_API_TOKEN = 'create-api-token',
    ACCESS_CUSTOMER_SUPPORT_CHAT = 'access-customer-support-chat'
}
export type APIUserPrivilege = {
    label: PrivilegeLabel;
    description: string;
    inheritedFromRole?: boolean; // TODO[AppSec] - make mandatory
};
export type APIConfigurablePrivilege = {
    label: PrivilegeLabel;
    description: string;
    'restricted-roles': OrgAccessLevel[];
    warning?: string;
};
// TODO[AppSec] - suspect values are never undefined, verify and fix
export type PrivilegesByUser = Record<ArdoqId, APIUserPrivilege[] | undefined>;
// TODO[AppSec] - suspect values are never undefined, verify and fix
export type PrivilegesByOrganization = Record<ArdoqId, APIUserPrivilege[] | undefined>;
export type CustomFields = Record<string, any>;
type APIComponentMetaData = {
    pending?: boolean;
    externalDocuments?: PersistedExternalDocument[];
};
export type APIComponentAttributes = Entity & MetaData & ArdoqBlob<CamelizedEntityTypeBlob<APIEntityType.COMPONENT>, APIComponentMetaData> & Named & Described & Versioned & Lockable & CustomFields & {
    _order: number;
    'component-key': string;
    color?: string;
    icon?: string;
    image?: string;
    model: ArdoqId;
    parent: ArdoqId | null;
    rootWorkspace: ArdoqId;
    shape?: string;
    /** The component type name (human-readable label, not an id). */
    type: string;
    typeId: ArdoqId;
};
/**
 * Transition type until we have scopeData in place, will be replaced with full
 * APIComponentAttributes.
 * Useful to avoid copying all attributes of components (esp. custom fields)
 * when our use-cases usually only require a few attributes.
 */
export type APIComponentAttributesLite = Pick<APIComponentAttributes, '_id' | 'rootWorkspace' | 'name' | 'lock' | 'typeId' | 'model' | '_version' | 'type'>;
type ReferenceMetaData = {
    sourceName?: string;
    targetName?: string;
    typeName?: string;
};
export enum MagicReferenceTypes {
    syntheticParentChild = -1,
    implicit = 2
}
export type APIReferenceAttributesCore = Entity & MetaData & ArdoqBlob<LispyEntityTypeBlob<APIEntityType.REFERENCE>, ReferenceMetaData> & Described & Versioned & {
    displayText?: string | null;
    model: ArdoqId;
    order: number;
    rootWorkspace: ArdoqId;
    source: ArdoqId;
    target: ArdoqId;
    targetWorkspace: ArdoqId;
    type: number;
};
export type APIReferenceAttributes = APIReferenceAttributesCore & CustomFields;
type VirtualParentType = {
    virtualType: 'ardoq_parent';
};
/**
 * Target is parent, child is the source. FE treats this the opposite way
 * Note the ardoq blob
 */
export type APIParentChildReferenceAttributes = 
// Note: this ardoq blob only has virtualType and 'entity-type' attributes
ArdoqBlob<LispyEntityTypeBlob<APIEntityType.REFERENCE>, VirtualParentType> & {
    /**
     * Combined id `childId:parentId`
     * Note that FE expects "-" as separator instead of ":"
     */
    _id: `${ArdoqId}:${ArdoqId}`;
    type: MagicReferenceTypes.syntheticParentChild;
    rootWorkspace: ArdoqId;
    source: ArdoqId;
    targetWorkspace: ArdoqId;
    target: ArdoqId;
};
/**
 * FE version of the parent-child reference.
 * Note differences:
 * - id has different format
 * - source and target are swapped
 *
 * See toFEParentChildFormat for conversion
 */
export type FEParentChildReferenceAttributes = Omit<APIParentChildReferenceAttributes, '_id'> & {
    _id: `${ArdoqId}-${ArdoqId}`;
};
export type VirtualReferenceAttributes = Entity & Described & {
    displayText?: string | null;
    model: ArdoqId;
    rootWorkspace: ArdoqId;
    source: ArdoqId;
    target: ArdoqId;
    targetWorkspace: ArdoqId;
    type: string;
    order: number;
};
export type AllSupportedAPIAttributes = APIComponentAttributes | APIReferenceAttributes;
export type AllSupportedEntityTypes = APIEntityType.REFERENCE | APIEntityType.COMPONENT;
export type MergeComponentApiResponse = {
    'deleted-component-ids': ArdoqId[];
    'new-parents': Record<ArdoqId, {
        _version: number;
        parent: ArdoqId;
    }>; // If we merge components with children, those children needs to have their parent updated to be the merged component. Record<childId, newParentId>
    'updated-component': APIComponentAttributes;
    'updated-references': APIReferenceAttributes[];
    'updated-tags': APITagAttributes[];
};
export type APITagAttributes = Entity & MetaData & ArdoqBlob & Named & Described & Versioned & {
    [ScopeDataCollection.COMPONENTS]: ArdoqId[];
    [ScopeDataCollection.REFERENCES]: ArdoqId[];
    rootWorkspace: ArdoqId;
};
export enum ScopeDataCollection {
    COMPONENTS = 'components',
    FIELDS = 'fields',
    MODELS = 'models',
    REFERENCES = 'references',
    TAGS = 'tags'
}
export type MergeReferenceApiResponse = {
    'deleted-reference-ids': ArdoqId[];
    'updated-reference': APIReferenceAttributes;
    'updated-tags': APITagAttributes[];
};
export const IMPLICIT_REFTYPE_ID = 2;
export const PARENT_REF_TYPE_NAME = 'Is Parent Of';
export type APIReferenceType = {
    color: string;
    description?: string | null;
    id: number;
    index?: number;
    line: LineType;
    lineBeginning?: ArrowType;
    lineEnding: ArrowType;
    name: string;
    returnsValue: boolean;
    svgStyle: string | null;
    hasCardinality?: boolean;
};
export type APIComponentType = Described & {
    children: Record<ArdoqId, APIComponentType>;
    color: string | null;
    icon: string | null;
    id: ArdoqId;
    image: string | null;
    index: number;
    level: number;
    name: string;
    parent?: string | null;
    returnsValue: boolean;
    shape: string | null;
    standard: string | null;
};
export type APIComponentTypeAttributes = Entity & MetaData & ArdoqBlob & Omit<APIComponentType, 'children'> & {
    modelId: ArdoqId;
};
export type APIReferenceTypeAttributes = Entity & MetaData & ArdoqBlob & APIReferenceType & {
    modelId: ArdoqId;
};
export type APIModelAttributes = Entity & MetaData & ArdoqBlob & Named & Described & Versioned & {
    'ardoq-persistent'?: {
        'use-case'?: string;
    } | null;
    blankTemplate: boolean;
    category: string;
    common?: boolean;
    createdFromTemplate?: ArdoqId;
    defaultViews: ViewIds[];
    flexible: boolean;
    maxReferenceTypeKey?: number;
    referenceTypes: Record<ArdoqId, APIReferenceType>;
    root: Record<ArdoqId, APIComponentType>;
    startView: ViewIds | null;
    useAsTemplate: boolean;
};
export interface MutableTemplate {
    name: string;
    flexible: boolean;
    category: string;
    startView: ViewIds | null;
    defaultViews: ViewIds[];
    description: string | null;
}
export type APITemplateAttributes = MutableTemplate & Entity;
export type NewTemplate = MutableTemplate & {
    model: ArdoqId;
};
export enum APIFieldType {
    CHECKBOX = 'Checkbox',
    DATE_ONLY = 'Date',
    DATE_ONLY_RANGE = 'DateOnlyRange',
    DATE_TIME = 'DateTime',
    DATE_TIME_RANGE = 'DateTimeRange',
    EMAIL = 'Email',
    FILE = 'File',
    LIST = 'List',
    NUMBER = 'Number',
    SELECT_MULTIPLE_LIST = 'SelectMultipleList',
    TEXT = 'Text',
    TEXT_AREA = 'TextArea',
    URL = 'Url',
    USER = 'User'
}
/**
 * Number format option for percentage fields.
 * Used with numberFormatOptions field attribute.
 */
export const NUMBER_FORMAT_PERCENTAGE = 'percentage';
export type DateRangeFieldDefaultValue = {
    start: string | null;
    end: string | null;
};
/**
 * Fields whos value is locally derived using transformations, optionally using existing fields from the same component
 */
export type LocallyDerivedFieldSettings = {
    locallyDerived: LocallyDerivedTransformation[];
    storedQueryId?: never;
};
/**
 * Fields whos value is a stored Gremlin query
 */
export type StoredQueryFieldSettings = {
    locallyDerived?: never;
    storedQueryId: ArdoqId;
};
/**
 * @see https://github.com/ardoq/ardoq-api/blob/29cbdf6e1d4c223ea276eb78c1341e4464be880e/src/ardoq/persistence/field_repo.clj#L136
 */
export type CalculatedFieldSettings = LocallyDerivedFieldSettings | StoredQueryFieldSettings;
type CalculatedFieldExtras = {
    lastRecalculation: string;
    lastRecalculationFailed: boolean;
};
export type DateTimeFieldAttribute = {
    _id: ArdoqId;
    type: APIFieldType.DATE_TIME;
    label: string;
    name: string;
};
export type FieldGlobalAttributes = Named & Described & {
    label: string;
    type: APIFieldType;
    calculatedFieldSettings?: CalculatedFieldSettings;
    defaultValue: string | boolean | null | DateRangeFieldDefaultValue | number;
    numberFormatOptions?: string;
};
export type APIFieldGlobalAttributes = FieldGlobalAttributes & {
    model: ArdoqId;
};
export type APIFieldAttributes = Entity & MetaData & ArdoqBlob & Named & Described & Versioned & APIFieldGlobalAttributes & {
    _order: number;
    createdByEmail?: Email;
    createdByName?: string;
    global?: boolean;
    globalref?: boolean;
    lastModifiedByEmail?: Email;
    lastModifiedByName?: string;
    referenceType: ArdoqId[];
    componentType: ArdoqId[];
    calculatedFieldExtras?: CalculatedFieldExtras;
};
/**
 * A string representing a date in ISO format
 * eg. "2021-01-01"
 */
export type PlainDateISO = string;
/**
 * A ISO 8601 string representing a date and time in UTC
 * eg. "2021-01-01T12:00:00Z"
 */
export type PlainDateTimeISO = string; // yyyy-mm-ddThh:mm:ssZ
export type APIDateOnlyFieldAttributes = Extend<APIFieldAttributes, {
    type: APIFieldType.DATE_ONLY;
    defaultValue: PlainDateISO | null;
  }>;
export type APIDateTimeFieldAttributes = Extend<APIFieldAttributes, {
    type: APIFieldType.DATE_TIME;
    defaultValue: PlainDateTimeISO | null;
  }>;
export enum Verb {
    DELETE = 'delete',
    CREATE = 'create',
    UPDATE = 'update',
    MERGE = 'merge'
}
export type APIUserDefaultAttributes = {
    _id: ArdoqId;
    name: string; // TODO: change to string | null
    email: Email; // TODO: change to Email | null
};
export type DateRangeFieldMapType = Map<string, Record<ArdoqId, {
    start: APIFieldAttributes;
    end: APIFieldAttributes;
}>>;
export type APIAttachment = Entity & MetaData & ArdoqBlob & Named & Described & Versioned & {
    folder: ArdoqId | null;
    size: number;
    filename: string;
    // in theory `content-type` is always defined, but
    // I got an error report from sentry where missing
    // 'content-type' was causing the issue
    'content-type'?: string;
    uri: string;
    // These fields not always present for global attachments
    rootWorkspace?: ArdoqId;
    locked?: null;
    /**
     * Per-attachment permission flag set by the backend on workspace-attachment
     * endpoints, mirroring the `:make-global` authorization policy
     * (ardoq-api `attachment_service.clj`). When `false`, the caller cannot
     * promote this attachment to org-level and the FE should disable the
     * Promote option in cross-workspace copy flows. Treat `undefined` as
     * `false` for fail-safe behaviour against older API responses.
     */
    canMakeGlobal?: boolean;
};
type APIFolderContent = Array<{
    type: ResourceType.REPORT | ResourceType.DASHBOARD | ResourceType.WORKSPACE | ResourceType.WORKSPACEFOLDER;
    _id: string;
}>;
export type APIFolderAttributes = Entity & MetaData & ArdoqBlob & Named & Described & Versioned & {
    parent: ArdoqId | null;
    resource: Resource;
    workspaces?: ArdoqId[];
    content?: APIFolderContent;
};
type Resource = {
    type: string;
    id: string;
};
export type APIContributor = Entity & Named & Omit<MetaData, 'created' | 'lastUpdated'> & {
    email: Email;
    /**
     * API has to resolve images with viewpoint-scope/resolve-images-for-users
     * which it is not consistent with.
     */
    image?: Url;
};
export type APIOrganizationUser = APIUserDefaultAttributes & {
    created: string;
    sessions: number;
    'last-login': string;
    role: OrgAccessLevel;
    serviceAccount: boolean;
    /**
     * The concept of "active" is part of the SCIM identity management specification:
     *
     * "...you deactivate user objects rather than deleting them. By deactivating
     * a user object, the user’s active status becomes false. Even though you can
     * still query the user object, the user can’t log in...with the deactivated
     * account. To allow a user to log in, you can reactivate their account.
     *
     * `active` is false if the user's org uses SCIM, and the user is deactivated.
     * Otherwise, the value is true."
     *
     * - https://help.salesforce.com/s/articleView?id=sf.identity_scim_deactivate_reactivate_user.htm&type=5
     */
    active: boolean;
};
export type ComponentTypeHierarchy = Record<ArdoqId, APIComponentType>;
export type APIRoleAttributes = Omit<Persisted, '_id'> & {
    ardoq: {
        entityType: 'role';
    };
    label: OrgAccessLevel;
    privileges: PrivilegeLabel[];
    title: string;
};
export type UpdatePrivilegesOperation = {
    type: 'update-privileges';
    roleLabel: OrgAccessLevel;
    privileges: Partial<Record<PrivilegeLabel, boolean>>;
};
export type RoleBulkUpdateRequest = {
    operations: UpdatePrivilegesOperation[];
};
export type ComponentHistoryResult = Omit<APIComponentAttributes, 'type'>;
export enum ReferenceDirection {
    UNSET = 'unset',
    OUTGOING = 'outgoing',
    INCOMING = 'incoming',
    NONE = 'none'
}
export enum GroupType {
    PARENT_ALL = 'PARENT_ALL',
    COMPONENT = 'COMPONENT',
    WORKSPACE = 'WORKSPACE',
    REFERENCE = 'REFERENCE',
    REFERENCE_CONTEXTUAL = 'REFERENCE_CONTEXTUAL',
    PARENT = 'PARENT',
    CHILD = 'CHILD',
    FIELD = 'FIELD',
    TAG = 'TAG',
    ALL = 'ALL',
    REFERENCE_TYPE_NAME = 'REFERENCE_TYPE_NAME'
}
type GroupingRuleBase = {
    direction?: ReferenceDirection;
    sublabel?: string;
    targetId?: string; // note that referenceTypeId is a number cast to string
    workspaceId?: string;
    label?: string;
};
type NonReferenceTypeNameGroupingRule = GroupingRuleBase & {
    type: Exclude<GroupType, GroupType.REFERENCE_TYPE_NAME>;
};
type ReferenceTypeNameGroupingRule = GroupingRuleBase & {
    type: GroupType.REFERENCE_TYPE_NAME;
    targetId: string;
};
export const isReferenceTypeNameGroupingRule = (rule: GroupingRule): rule is ReferenceTypeNameGroupingRule => {
    return rule.type === GroupType.REFERENCE_TYPE_NAME;
};
export type GroupingRule = NonReferenceTypeNameGroupingRule | ReferenceTypeNameGroupingRule;
type TopToBottom = 'top-to-bottom';
type LeftToRight = 'left-to-right';
type RightToLeft = 'right-to-left';
type BottomToTop = 'bottom-to-top';
export type LayoutOrientation = TopToBottom | LeftToRight | RightToLeft | BottomToTop;
export enum BooleanOperator {
    AND = 'AND',
    OR = 'OR'
}
export type ViewpointSurveyDetail = {
    componentTypeName: string;
    surveys: ArdoqId[];
};
export type SelectedDetailEntry = {
    scope: 'selected';
    useFieldDescriptions?: boolean;
    includeTags?: boolean;
    selectedFields: {
        name: string;
        category: 'field' | 'attribute';
    }[];
};
type NoneDetailEntry = {
    scope: 'none';
    useFieldDescriptions?: boolean;
    includeTags?: boolean;
};
export type AllDetailEntry = {
    scope: 'all';
    useFieldDescriptions?: boolean;
    includeTags?: boolean;
    selectedFields?: {
        name: string;
        category: 'field' | 'attribute';
    }[];
};
export type ComponentTypeDetailEntry = (AllDetailEntry & {
    componentTypeName: string;
    }) | (NoneDetailEntry & {
    componentTypeName: string;
    }) | (SelectedDetailEntry & {
    componentTypeName: string;
});
export type ReferenceTypeDetailEntry = (AllDetailEntry & {
    referenceTypeName: string;
    }) | (NoneDetailEntry & {
    referenceTypeName: string;
    }) | (SelectedDetailEntry & {
    referenceTypeName: string;
});
export type ViewpointDetailEntry = ComponentTypeDetailEntry | ReferenceTypeDetailEntry;
export type EntryPoint = [
    string,
    string
];
export type DefaultForComponentTypeName = {
    componentTypeName: string;
    workspaceId: ArdoqId;
};
export type Triple = [
    string,
    string,
    string
];
export type Direction = 'incoming' | 'outgoing' | 'undirected';
export type Constraint = {
    triple: Triple;
    direction: Direction;
};
type ViewpointNetworkViewStyle = {
    style: 'relationship-view';
};
export type ViewpointDependencyMapViewStyle = {
    style: 'dependency-map';
    layoutOrientation: TopToBottom | LeftToRight;
};
export type ViewpointSimpleBlockDiagramViewStyle = {
    style: 'simple-block-diagram';
    layoutOrientation: LayoutOrientation;
    defaultGroupingCollapseState: GroupingCollapseState;
};
export type TimelineField = {
    name: string;
};
export enum UnitOfTime {
    MILLISECOND = 'ms',
    SECOND = 'seconds',
    MINUTE = 'minutes',
    HOUR = 'hours',
    DAY = 'days',
    WEEK = 'weeks',
    MONTH = 'months',
    QUARTER = 'quarters',
    YEAR = 'years'
}
export type TimelinesGridPrecisionUnit = UnitOfTime.DAY | UnitOfTime.WEEK | UnitOfTime.MONTH | UnitOfTime.QUARTER | UnitOfTime.YEAR;
export type GridPrecisionAutoOptionValue = '*';
export type TimelineGridPrecisionOptionValue = TimelinesGridPrecisionUnit | GridPrecisionAutoOptionValue;
export type DateRangeViewSetting = [
    string | Date | null,
    string | Date | null
] | RelativeDateValue;
export type ViewpointTimelineViewStyle = {
    style: 'timeline';
    fields: TimelineField[];
    gridPrecision?: TimelineGridPrecisionOptionValue;
    domainRange?: DateRangeViewSetting;
    defaultGroupingCollapseState: GroupingCollapseState;
};
export type ViewpointViewStyle = ViewpointNetworkViewStyle | ViewpointDependencyMapViewStyle | ViewpointSimpleBlockDiagramViewStyle | ViewpointTimelineViewStyle;
export enum GroupingCollapseState {
    EXPAND_ALL = 'expand_all',
    COLLAPSE_ALL = 'collapse_all'
}
export type LabeledValue<T = any> = {
    value: T;
    label: string;
};
export type DiscoverTheme = 'ardoq';
export type DiscoverLayout = {
    powerUserEmails: Email[];
    theme: DiscoverTheme | string;
    headerImage: string | null;
    useOrgLogo: boolean;
    message: string;
    showRecentActivity: boolean;
    showMyTasks: boolean;
    showDataInventory?: boolean;
    showMyTasksInNav?: boolean;
    showDataInventoryInNav?: boolean;
    discoverLandingLogo: string | null;
    customSearchField?: string;
    customSearchFieldLabel: string;
    disableArdoqIdSearch: boolean;
    searchQuickFilterComponentTypeNames: string[];
    useSemanticSearch?: boolean;
};
export type APIWelcomeTextAttributes = Entity & Described & {
    discover: Partial<DiscoverLayout>;
};
export type APIDeletedComponentAttributes = APIComponentAttributes & {
    ancestors: ArdoqId[];
    descendants: ArdoqId[];
};
export type RestoreDeletedResponse = {
    components: APIDeletedComponentAttributes[];
    references: APIReferenceAttributes[];
};
// These are synonymous with "comparators" and are exclusive to the frontend.
//
// typia compiles the JSDoc here into the AI conditional-formatting tool's
// JSON schema, so these comments are LLM-facing — keep them accurate.
export enum FilterInfoOperator {
    /**
     * Exact match on an id or select-style field — the entity's id, the `type`
     * field (matched against a type name), or a select-list value. `value`: a
     * non-empty string.
     */
    ID_MATCH = 'idMatch',
    /** Matches when the date field equals the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_MATCH = 'dateMatch',
    /** Matches when the date field is before the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_EARLIER = 'dateEarlier',
    /** Matches when the date field is after the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_LATER = 'dateLater',
    /** Matches when the checkbox field is ticked. `value`: the literal `true`. */
    BOOLEAN = 'boolean',
    /** Matches when the field equals the rule's `value`. `value`: a string or number. */
    EQUALS = 'equals',
    /** Matches when the numeric field is less than the rule's `value`. `value`: a number. */
    LESS_THAN = 'lessThan',
    /** Matches when the numeric field is greater than the rule's `value`. `value`: a number. */
    GREATER_THAN = 'greaterThan',
    /** Matches when the text field contains the rule's `value` as a substring. `value`: a string. */
    CONTAINS = 'contains',
    /** Matches when the date-range field contains the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_CONTAINS = 'dateRangeContains',
    /** Matches when the date-range field does not contain the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_DOES_NOT_CONTAIN = 'dateRangeDoesNotContain',
    /** Matches when the date-range field lies entirely before the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_BEFORE = 'dateRangeBefore',
    /** Matches when the date-range field lies entirely after the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_AFTER = 'dateRangeAfter'
}
// typia compiles the JSDoc here into the AI conditional-formatting tool's
// JSON schema, so these comments are LLM-facing — keep them accurate.
export enum FormattingRuleOperator {
    /** Paints a gradient / heatmap across a numeric field — use only when the user asks for a gradient or heatmap. `value`: a colour-range object `{ kind: "color-range", mode, min, mid, max }`, where `mode` is `"number"` (the default; use `"percent"` only for percentage fields or when the user asks) and each of `min`/`mid`/`max` is `{ color, value }`. All three stops are required — omitting any renders nothing. When the user gives no thresholds, derive them from the field's numeric distribution in the summary: `min.value` from the minimum, `max.value` from the maximum, `mid.value` from the mean (or the midpoint). Pick a three-stop colour ramp running from cool/light (low) to warm/dark (high). */
    COLOR_RANGE = 'colorRange',
    /** Matches when the entity's id does not equal the rule's `value`. `value`: a non-empty string. */
    ID_DOES_NOT_MATCH = 'idDoesNotMatch',
    /** Matches when the field does not equal the rule's `value`. `value`: a string or number. */
    NOT_EQUAL = 'notEqual',
    /** Matches when the text field does not contain the rule's `value` as a substring. `value`: a string. */
    NOT_CONTAINS = 'notContains',
    /** Matches when the checkbox field is not ticked. `value`: the literal `true`. */
    NOT_BOOLEAN = 'notBoolean',
    /** Matches when the numeric field is greater than or equal to the rule's `value`. `value`: a number. */
    GREATER_THAN_OR_EQUAL_TO = 'greaterThanOrEqualTo',
    /** Matches when the numeric field is less than or equal to the rule's `value`. `value`: a number. */
    LESS_THAN_OR_EQUAL_TO = 'lessThanOrEqualTo',
    /** Matches when the numeric field is within an inclusive range. `value`: a `[number, number]` pair, `[min, max]`. */
    BETWEEN = 'between',
    /** Matches when the numeric field is outside the range. `value`: a `[number, number]` pair, `[min, max]`. */
    NOT_BETWEEN = 'notBetween',
    /** Matches when the date field is within a range. `value`: a two-element array of ISO date strings, `["YYYY-MM-DD", "YYYY-MM-DD"]` (start, end). */
    DATE_BETWEEN = 'dateBetween',
    /** Matches when the date field is outside a range. `value`: a two-element array of ISO date strings, `["YYYY-MM-DD", "YYYY-MM-DD"]` (start, end). */
    DATE_NOT_BETWEEN = 'dateNotBetween',
    /** Matches when the date field does not equal the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_NOT_EQUAL = 'dateNotEqual',
    /** Matches when the date field is on or before the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_ON_OR_BEFORE = 'dateOnOrBefore',
    /** Matches when the date field is on or after the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"`. */
    DATE_ON_OR_AFTER = 'dateOnOrAfter',
    /** Matches when the date-range field ends on or before the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_ON_OR_BEFORE = 'dateRangeOnOrBefore',
    /** Matches when the date-range field starts on or after the rule's `value`. `value`: an ISO date string `"YYYY-MM-DD"` or a relative-date object such as `{ "period": "this", "unit": "month" }`. */
    DATE_RANGE_ON_OR_AFTER = 'dateRangeOnOrAfter',
    /** Matches when the date-range field falls within a range. `value`: a two-element array of ISO date strings, `["YYYY-MM-DD", "YYYY-MM-DD"]` (start, end). */
    DATE_RANGE_BETWEEN = 'dateRangeBetween',
    /** Matches when the date field falls in a relative period. `value`: a relative-date object such as `{ "period": "last", "unit": "week" }` or `{ "period": "inTheNext", "unit": "day", "amount": 7 }`. */
    RELATIVE_DATE = 'relativeDate',
    /** Matches when the field is empty (has no content). `value`: `null`. */
    IS_EMPTY = 'isEmpty',
    /** UNSUPPORTED in modern conditional formatting — do NOT use. For an emptiness check use `isEmpty` instead. */
    IS_NOT_EMPTY = 'isNotEmpty',
    /** Matches when the list field contains at least one of the strings in the rule's `value`. `value`: a non-empty array of strings. */
    LIST_CONTAINS = 'listContains',
    /** Matches when the list field contains none of the strings in the rule's `value`. `value`: a non-empty array of strings. */
    LIST_DOES_NOT_CONTAIN = 'listDoesNotContain'
}
export type FormattingOperator = FilterInfoOperator | FormattingRuleOperator;
export enum DiffMode {
    SCENARIO = 'scenario',
    DIFF = 'diff',
    MAIN = 'main'
}
export enum Operator {
    /**
     * Simple case insensitive substring matching. eg.
     * "app" => "App", "app", "application", "Apple", "Pineapple"
     * Use in perspectives, not viewpoints
     */
    CONTAINS = 'contains',// TODO: Because contains_terms was introduced without migrating stored queryies with `contains`, we now have to support both until a proper fix is found
    /**
     * Use in perspectives, not viewpoints
     */
    NOT_CONTAINS = 'not_contains',
    /**
     * Search for a string of letters anywhere in a sentence, eg.
     * "new app" => "New app", "EA new app", "New app for EA team",
     *              "New application for EAs", and "New apps"
     * Use in viewpoints, not perspectives
     */
    CONTAINS_SUBSTRING = 'contains_substring',
    /**
     * Use in viewpoints, not perspectives
     */
    NOT_CONTAINS_SUBSTRING = 'not_contains_substring',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
    GREATER = 'greater',
    GREATER_OR_EQUAL = 'greater_or_equal',
    LESS = 'less',
    LESS_OR_EQUAL = 'less_or_equal',
    /**
     * @deprecated Use the more descriptive BETWEEN_INCLUSIVE instead
     */
    BETWEEN = 'between',
    BETWEEN_INCLUSIVE = 'between_inclusive',
    EQUAL = 'equal',
    NOT_EQUAL = 'not_equal',
    IN = 'in',
    CHECKED = 'checked',// API should get EQUAL + value: true
    UNCHECKED = 'unchecked',// API should get EQUAL + value: false
    UNSET = 'UNSET',// API should get IS_EMPTY
    FROM = 'from',
    TO = 'to',
    /**
     * Cannot be deleted because users may have bookmarked urls with `LoadedState`s containing this operator.
     * @deprecated Due to transition to Postgres for Advanced Search
     */
    DO_NOT_USE_CONTAINS_PHRASE = 'contains_phrase',
    /**
     * Cannot be deleted because users may have bookmarked urls with `LoadedState`s containing this operator.
     * @deprecated Due to transition to Postgres for Advanced Search
     */
    DO_NOT_USE_NOT_CONTAINS_PHRASE = 'not_contains_phrase'
}
/**
 * This is a type of {@link Record} where some keys are required and some keys are not required.
 *
 * using Partial Records keeps us honest, since we don't have `noUncheckedIndexedAccess` turned on in tsconfig
 */
type PartialRecord<Optional extends keyof any, Required extends keyof any, V> = Partial<Record<Optional, V>> & Record<Required, V>;
export const EMPTY = 'No value';
// typia compiles the JSDoc below into the AI conditional-formatting tool's JSON
// schema (ardoq-front `ApplyConditionalFormattingPayload`); treat it as code.
/**
 * The formatting applied to a single value of a list/select field within a
 * list-field colour map.
 */
export type ColorFormatting = {
    /** A hex colour from Ardoq's conditional-formatting palette, same palette as a rule's top-level `color`. */
    color: string;
    /** `true` to hide (not colour) entities with this value. */
    isDisabled: boolean;
};
// typia compiles the JSDoc below into the AI conditional-formatting tool's JSON
// schema (ardoq-front `ApplyConditionalFormattingPayload`); treat it as code.
/**
 * Colours a list/select field by its values: a map from each list value to its
 * `ColorFormatting`. Colours every value of one field in a single rule, rather
 * than one rule per value. The `"No value"` key colours entities that have no
 * value for the field and must always be present.
 */
export type ListFieldFormatting = PartialRecord<string, typeof EMPTY, ColorFormatting>;
export const COLOR_RANGE_KIND = 'color-range';
export type ColorRangeType = 'number' | 'percent' | 'percentile';
export type ColorRangePoint = {
    value: number | null;
    color: string;
};
export type ColorRangeMidPoint = {
    value?: number | null;
    color: string;
};
// typia compiles the JSDoc below into the AI conditional-formatting tool's JSON
// schema (ardoq-front `ApplyConditionalFormattingPayload`); treat it as code.
/**
 * A numeric gradient (heatmap): colours entities along a min → mid → max scale
 * by the value of a numeric field.
 */
export type ColorRangeFormatting = {
    kind: typeof COLOR_RANGE_KIND;
    mode: ColorRangeType;
    min: ColorRangePoint;
    mid: ColorRangeMidPoint;
    max: ColorRangePoint;
};
/**
 * The `value` field in filter entities.
 * Not all filter types can have all of these, so this needs to be cleaned up and narrowed down to the
 * specific types allowed for conditional formatting v0, conditional formatting v1, advanced search, etc.
 */
export type QueryBuilderRuleValue = null // for operator Operator.IS_EMPTY and Operator.IS_NOT_EMPTY
 | string | boolean | number | string[] // date range start and end (for "between" operator), or selected values in a multi-select list field formatting rule.
 | [
    number,
    number
] // number field formatting rule with a "between" or "not between" operator.
 | ListFieldFormatting // formatting info by list field value
 | ColorRangeFormatting | RelativeDateValue;
export type TripleDirection = 'outgoing' | 'incoming';
/**
 * Standard triple with a direction
 */
export type DirectedTriple = {
    sourceType: string;
    targetType: string;
    referenceType: string;
    direction: TripleDirection;
};
/**
 * Is a local id unique to a single traversal request. Follows the pattern
 * in the inference engine to make it possible to reuse a single filter
 * for different nodes. It can be a simple counter with e.g. a prefix,
 * something like `f-1`, `f-2`, `f-3`, etc.
 */
export type LocalFilterId = string;
// `excluded` is currently not used, but supported on the BE. We might add it in the future
export type TripleQualifier = 'optional' | 'required' | 'excluded';
export type DirectedTripleWithFilters = {
    sourceType: string;
    targetType: string;
    referenceType: string;
    direction: TripleDirection;
    qualifier?: TripleQualifier;
    sourceFilter?: LocalFilterId;
    targetFilter?: LocalFilterId;
    referenceFilter?: LocalFilterId;
    isVirtual?: true;
    referenceTypeId?: ArdoqId;
};
export type DirectedTripleWithFiltersWithGraphIds = DirectedTripleWithFilters & {
    sourceGraphId: string;
    targetGraphId: string;
    referenceGraphId: string;
};
export type APIGlobalField = {
    readonly label: string;
    readonly name: string;
    readonly defaultValue: DateRangeFieldDefaultValue | string | null | number | boolean;
    readonly type: APIFieldType;
    readonly calculatedFieldSettings?: CalculatedFieldSettings;
    readonly numberFormatOptions?: string;
    readonly description?: string | null;
};
export enum SearchBackend {
    ADVANCED_SEARCH = 'advanced-search',
    GREMLIN = 'gremlin',
    GRAPHLAKE = 'graphlake'
}
export enum SearchType {
    QUERY = 'query',
    PARAMETER_QUERY = 'parameterQuery',
    METAMODEL_QUERY = 'metamodelQuery',
    DYNAMIC_FILTER_QUERY = 'dynamicFilterQuery',
    CALCULATED_FIELD_QUERY = 'calculatedFieldQuery',
    BROADCAST_AUDIENCE_QUERY = 'broadcastAudienceQuery'
}
export type APIToken = {
    _id: string;
    tokenExpirationDate: string;
    lastRegenerated: string;
    description: string;
    type: 'api';
    token?: string;
    ardoq: {
        'entity-type': 'token';
    };
    'user-id': string;
    'user-name': string;
    'user-email': Email;
    'last-used': string;
};
export type APIServiceAccountUser = APIUserDefaultAttributes & MetaData & {
    roleLabel: string;
};
export type APIServiceAccount = {
    user: APIServiceAccountUser;
    token: APIToken;
};
export type CreateServiceAccountAndTokenPayload = Named & {
    role: OrgAccessLevel;
    tokenDescription: string;
};
export type CopyConfig = {
    useCase?: Entity[];
    entityGroup?: Entity[];
    componentType?: Entity[];
    referenceType?: Entity[];
    field?: Entity[];
    workspace?: Entity[];
    survey?: Entity[];
    presentation?: Entity[];
    perspective?: Entity[];
    dashboard?: Entity[];
    tabularMapping?: Entity[];
    servicenowConfig?: Entity[];
    broadcast?: Entity[];
    viewpoint?: Entity[];
    storedQuery?: Entity[];
    report?: Entity[];
    traversal?: Entity[];
    page?: Entity[];
};
export type BundleShape = {
    name: string;
    copyConfig: CopyConfig;
};
export type PersistedBundleShape = Persisted<BundleShape> & Versioned;
export type TypeRelationship = {
    sourceType: string;
    sourceWorkspace: ArdoqId;
    referenceType: string;
    targetType: string;
    targetWorkspace: ArdoqId;
};
export type TypeRelationshipsResult = {
    readonly componentTypes: string[];
    readonly referenceTypes: string[];
    readonly typeRelationships: TypeRelationship[];
};
export type APIRequestAccessResponse = Entity[];
export enum SortOrder {
    ASC = 'ASC',
    DESC = 'DESC',
    DEFAULT = 'DEFAULT'
}
export type SortParams = {
    sortOrder: SortOrder;
    sortByField: string | null;
};
export type ComparableAPIEntityTypes = APIEntityType.COMPONENT | APIEntityType.REFERENCE | APIEntityType.TAG;
export interface ModelUsagePageType {
    id: string;
    'used-by-components': number;
}
export interface ModelUsage {
    'page-types': ModelUsagePageType[];
    workspaces: {
        _id: string;
        name: string;
    }[];
}
export type UnknownDocumentType = {
    _id: ArdoqId;
    created: string;
    name: string;
    rootWorkspace: ArdoqId | undefined;
    'last-updated': string;
};
export interface AdvancedSearchField {
    name: string;
    label: string;
    type: APIFieldType;
}
export interface AdvancedSearchResultField extends AdvancedSearchField {
    _id: ArdoqId;
}
export interface SearchFieldsResult {
    fields: AdvancedSearchResultField[];
    total: number;
}
export type PersistedInvitation = Persisted<{
    email: Email;
    /** Date string */
    'invited-at': string;
    /** User IDs */
    'invited-by': ArdoqId;
    'invited-to': ArdoqId;
    role: OrgAccessLevel;
    permissions: Record<'admin' | 'read' | 'write', never[]>;
    privileges: PrivilegeLabel[];
}>;
export type CreateInvitationsOpts = Pick<PersistedInvitation, 'role' | 'privileges'> & {
    emails: Email[];
};
export const allIPAllowlistTypes = ['api', 'any'] as const;
export type AllowlistType = 'api' | 'any';
export type IPAllowlistEntry = {
    address: string;
    maskSize: number;
    // These types specify what kind of request a allowlist applies to. A
    // allowlist of type "api" only affects requests authenticated using API
    // tokens, while a allowlist of type "any" affects all requests.
    whitelistType: AllowlistType;
    description: string;
};
export type PersistedIPAllowlistEntry = Entity & MetaData & ArdoqBlob & IPAllowlistEntry;
export type ComponentsMembershipsMap = {
    [componentId: string]: ArdoqId[];
};
export type HexColor = string;
export type Palette = {
    label?: string;
    paletteColors: HexColor[];
};
type OriginEntity = {
    id: ArdoqId;
    _version?: number;
};
type BranchInfo = {
    [entityType: string]: {
        [newEntityId: string]: OriginEntity;
    };
};
type SkippedEntities = {
    [entityType: string]: ArdoqId[];
};
export type DependentEntityId = ArdoqId;
export type DependentEntityTypes = {
    [dependentEntityType: string]: DependentEntityId[];
};
export type MissingEntityTypeDetails = {
    [missingEntityId: string]: DependentEntityTypes;
};
export type MissingDependencyIds = {
    [missingEntityType: string]: MissingEntityTypeDetails;
};
export type CopyBundleResponse = {
    missingDependencyIds: MissingDependencyIds;
    branchInfo: BranchInfo;
    workspacefolders: ArdoqId[];
    skippedEntities: SkippedEntities;
};
export type CopyAsyncBundleResponse = {
    requestId: string;
};
export type ValidateBundleResponse = {
    missingDependencyIds: MissingDependencyIds;
};
export type IncompatibleField = {
    fieldName: string;
    fieldLabel: string;
    sourceType: string;
    targetType: string;
};
export type ValidateCopyBundleResponse = {
    missingDependencyIds: MissingDependencyIds;
    incompatibleFields: IncompatibleField[];
};
export type TargetOrgMembersPermissions = PermissionAccessLevel.READ | PermissionAccessLevel.EDIT;
export type CopyBundleBody = {
    targetOrg: string;
    dryRun: boolean;
    branch: boolean;
    copyGlobalAttachments: boolean;
    targetOrgMembersPermissions?: TargetOrgMembersPermissions;
};
export type ValidateCopyBundleBody = {
    targetOrg: string;
};
export type RemoveReferenceSuggestion = {
    action: 'remove-reference';
    data: ArdoqId;
    'violating-path': {
        vertices: APIComponentAttributes[];
        edges: APIReferenceAttributes[];
    };
};
export type AddReferenceSuggestion = {
    action: 'add-reference';
    data: {
        source: ArdoqId;
        target: ArdoqId;
        reference: {
            type: string;
        };
        rootWorkspace: ArdoqId;
        targetWorkspace: ArdoqId;
    };
};
export type InferenceSuggestion = (AddReferenceSuggestion | RemoveReferenceSuggestion)[];
export type APIInferenceSuggestionResult = Record<ArdoqId, InferenceSuggestion>;
export type APIDocumentArchiveFolder = Entity & Versioned & MetaData & Named & ArdoqBlob & {
    resource: {
        id: ArdoqId;
        type: 'workspace' | 'org';
    };
    parent?: ArdoqId | null;
    origin?: Entity | null;
};
export type TransformToGlobalResponse = {
    folders: APIDocumentArchiveFolder[];
    attachments: APIAttachment[];
};
export type TransformToGlobalPayload = {
    workspaceId: ArdoqId;
    attachmentAndFolderIds: {
        attachmentIds: ArdoqId[];
        folderIds: ArdoqId[];
    };
};
export enum SSOMappingAction {
    ADD_TO_GROUP = 'add-to-group'
}
export enum SSOMappingCondition {
    EMPTY = '',
    ELEMENT_CONTAINS = 'element-contains',
    ELEMENT_EQUALS = 'element-equals',
    STRING_CONTAINS = 'string-contains',
    STRING_EQUALS = 'string-equals'
}
export type CurrentClaimsAPIResponse = {
    ts: string;
    'idp-user': {
        id: string;
        role: string | null;
        email: Email;
        name: string;
    } & {
        [k: string]: string | string[];
    };
};
export enum AddToGroupAction {
    JOIN = 'join-groups',
    LEAVE = 'leave-groups'
}
export type SSOAttributeMappingPreviewAPIResponse = {
    [k in SSOMappingAction]: {
        [k in AddToGroupAction]: ArdoqId[];
    };
} & {
    'matching-mappings': MappingRule[];
};
export type ActionAndClause = {
    action: Action;
    clause: Clause;
};
export type SSOAttributeMappingAPIResponse = Entity & MetaData & ActionAndClause;
export type LoginOptionWithEnabledFlag = {
    type: LoginOptions;
    uri: string;
    name: string;
    _id: string;
    enabled: boolean;
};
export type AggregatedSSOMappingsAPIResponse = {
    mappings: SSOAttributeMappingAPIResponse[];
    loginOptions: LoginOptionWithEnabledFlag[];
    groups: PermissionGroup[];
    currentClaims: CurrentClaimsAPIResponse;
};
export type TempId = string;
export type MappingRule = {
    _id: ArdoqId | TempId;
    action: Action;
    clause: Clause;
    created: Date;
};
export type Action = {
    action: SSOMappingAction;
    groupId: ArdoqId;
};
export type Clause = {
    value: string;
    attribute: string;
    condition: SSOMappingCondition;
    provider?: ArdoqId;
};
export type TypeId = string;
export type ReferenceTypesInUseResponse = {
    'model-id': ArdoqId;
    'types-in-use': number[];
};
export type SubtreeInfo = {
    numComponents: number;
    numIncidentReferences: number;
};
export type UserWithRoles = {
    name: string;
    email: Email;
    _id: ArdoqId;
    roles: string[];
    orgRole: OrgAccessLevel;
};
export type Webhook = Persisted<{
    name: string;
    url: string;
}>;
export type RegisterWebhookPayload = Pick<Webhook, 'name' | 'url'>;
export type EmailPreferenceConsent = {
    newsletterEmails: boolean;
    productEmails: boolean;
    onboardingEmails: boolean;
};
export type BulkUpdatedUsersRolePayload = Record<ArdoqId, {
    setRole: string;
  }>;
export type BulkUpdatedUsersPrivilegePayload = Record<ArdoqId, {
    setPrivileges: SetPrivilege;
  }>;
export type SetPrivilege = Partial<Record<PrivilegeLabel, {
    enabled: boolean;
}>>;
export type BulkUpdatedUsersCombinedPayload = Record<ArdoqId, {
    setPrivileges: SetPrivilege;
    setRole: string;
  }>;
export type BulkRemoveUsersPayload = Record<ArdoqId, {
    revokeMembership: boolean;
  }>;
export type APIScenarioAttributes = Entity & Named & Versioned & MetaData & {
    folder: ArdoqId | null;
    branchId: ArdoqId;
    scopeId: ArdoqId;
};
// Any asset can be contained in a folder.
export type ContentByType = Record<AssetType, ArdoqId[]>;
export type APIWorkspaceFolder = Entity & Named & Versioned & MetaData & {
    content: ContentItem[];
    description: string;
};
export type AssetFolder = APIWorkspaceFolder & {
    contentByType: ContentByType;
    nestedContent: ContentItem[];
    nestedContentByType: ContentByType;
    meta: {
        [key: string]: any;
    };
};
export type ContentItem = {
    _id: ArdoqId;
    type: AssetType;
};
export type LoginOption = {
    type: LoginOptions;
    uri: string;
    name: string;
    _id?: string;
    bustIframe?: boolean;
};
export type LoginConfig = {
    'login-options': LoginOption[];
    subdomain?: string | null;
    'login-page-v2-enabled': boolean;
    'login-page-v2-marketing-disabled': boolean;
    'autologin-option'?: string;
    'network-access'?: {
        blocked: true;
        message: string;
      } | {
        blocked: false;
        message: never;
    };
};
export type ChooseOrgOptions = {
    email: Email;
    organizations: Organization[];
};
export type CopyComponentsResponse = {
    components: Record<string, APIComponentAttributes>;
    references: Record<string, APIReferenceAttributes>;
    fields: APIFieldAttributes[];
    tags: APITagAttributes[];
    model: APIModelAttributes;
};
/**
 * Per-attachment choice the FE sends along with a cross-workspace copy when
 * File-type fields reference workspace-scoped attachments in the source.
 *  - 'strip': BE nils out the File field value on the copy (default if omitted).
 *  - 'promote': BE converts the attachment to org-level so it stays referenceable.
 *  - 'duplicate': BE copies the attachment binary into the target workspace
 *    and remaps the field value to the new attachment id.
 */
export type AttachmentStrategy = 'strip' | 'promote' | 'duplicate';
export type AttachmentStrategies = Record<ArdoqId, AttachmentStrategy>;
export type RestoreChangePayload = {
    entityId: string;
    fieldName: keyof APIComponentAttributes | keyof APIReferenceAttributes;
    restorableValue: APIComponentAttributes[keyof APIComponentAttributes] | APIReferenceAttributes[keyof APIReferenceAttributes];
    errorTraceId?: string;
};
export type ComponentOptionsResponse = {
    components: APIComponentAttributes[];
    total: number;
};
export type APIWorkspaceOverview = VersionedEntity & Named & Omit<MetaData, 'lastModifiedBy' | 'lastModifiedByEmail' | 'lastModifiedByName'> & {
    componentCount: number;
    componentTypeIds: ArdoqId[];
    deepLastModifiedByEmail: Email;
    deepLastModifiedByName: string;
    deepLastUpdated: string;
    gremlinEnabled: boolean;
    referenceCount: number;
};
export type DocumentArchiveRenamePayload = {
    typeId: ArdoqId;
    type: 'org' | 'workspace';
    renameRequest: RenameRequest;
};
export type UploadFilesPayload = {
    files: File[];
    type: 'org' | 'workspace';
    typeId: ArdoqId;
    folderId?: ArdoqId | null;
};
export type SetGremlinEnabledPayload = {
    workspaceIds: ArdoqId[];
    gremlinEnabled: boolean;
};
export type SetViews = {
    workspaceId: ArdoqId;
    views: ViewIds[];
};
export type StatusResponse = {
    analytics: string | null;
    'login-options': Record<LoginOptions, boolean>;
    'custom-authentication'?: string;
    'base-url': string;
    'support-url': string | null;
    'custom-domain': CustomDomain;
    version: string | null;
};
export enum LoginConfigurationKey {
    // SAML
    IDP_URI = 'idp-uri',
    IDP_CERT = 'idp-cert',
    // OAUTH
    CLIENT_ID = 'client-id',
    DISCOVERY_URL = 'well-known-discovery-url',
    TOKEN_URL = 'access-token-url',
    AUTHORIZATION_URL = 'authorization-url',
    USER_LOOKUP_URL = 'user-lookup-url'
}
export type LoginSetting = {
    _id?: string;
    type: LoginOptions;
    name: string;
    enabled: boolean;
    config?: Partial<Record<LoginConfigurationKey, string>>;
    ssoBased: boolean;
};
export type LoginSettingsResponse = {
    loginOptions: LoginSetting[];
    autologinEnabled: boolean;
};
export type ChangePasswordRequest = {
    current: string;
    password: string;
    confirm: string;
};
export type ResetPasswordResponse = {
    status: string;
    message: string;
};
export type ChangeEmailRequest = {
    currentPassword: string;
    email: string;
};
export type PartialUpdateValues = {
    oldValue: unknown;
    newValue: unknown;
    field: string;
}[];
export type PartialBulkUpdateValues = {
    componentId: ArdoqId;
    updates: PartialUpdateValues;
}[];
export type PartialBulkUpdatePayload = {
    componentId: ArdoqId;
    oldValue: unknown;
    newValue: unknown;
    field: string;
}[];
type SourceBundle = {
    _id: ArdoqId;
    name: string;
};
export type ArdoqPersistent = {
    'bundle-copy'?: {
        at: string;
        'source-bundle': SourceBundle;
        'source-org-label': string;
    };
    bundleCopy?: {
        at: string;
        sourceBundle: SourceBundle;
        sourceOrgLabel: string;
    };
};
export type WithArdoqPersistent = {
    'ardoq-persistent'?: ArdoqPersistent;
    ardoqPersistent?: ArdoqPersistent;
};
export type LinkedComponent = {
    referenceId: ArdoqId;
    componentId: ArdoqId;
};
/**
 * In our graph representation we have source and target maps with linked
 * components. To build paths of component we add the direction to the linked
 * component.
 */
type LinkedComponentWithDirection = LinkedComponent & {
    direction: ReferenceDirection;
    isParentChildReference: boolean;
};
export type ComponentPath = LinkedComponentWithDirection[];
export type PathResult = {
    /**
     * The index of the path in the request body, used to identify which path the
     * result belongs to.
     */
    pathIndex: number;
    /**
     * The component id of the start component of the path traversal.
     */
    startComponentId: ArdoqId;
    /**
     * The results of the path traversal, represented as an array of reference ids.
     */
    pathResults: ArdoqId[][];
};
